import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { GET_DIVISIONS } from "../../components/division/queries/GetDivisions";
import { NavLink, useLocation, useParams } from "react-router-dom";
import PaginatedCardList from "../../components/pagination/PaginatedCardList";
import NavBar from "../../components/common/NavBar";
import { toast } from "react-toastify";
import ArchiveDivisionMutation from "../../components/division/mutations/ArchiveDivision";
import AddDivisionModal from "../../components/division/AddDivisionModal";
import EditDivisionModal from "../../components/division/EditDivisionModal";
import { confirmDelete } from "../../components/common/confirmDelete";
import { SportIcon } from "../../components/common/SportIcon";
import useActiveProfile from "../../components/common/hooks/useActiveProfile";
import useCheckLimitOrPrompt from "../../components/common/hooks/useCheckLimitOrPrompt";
import { alphaFromHex } from "../../util/colors";
import useThemeColors from "../../components/organization/portal/useThemeColors";
import useTranslation from "../../i18n/useTranslation";
import OrganizationDivisionSummaryCharts from "../../components/organization/OrganizationDivisionSummaryCharts";
import DivisionStatusFilter, {
    DIVISION_STATUS_FILTERS,
} from "../../components/division/DivisionStatusFilter";
import { getLocalizedGraphQLError } from "../../util/graphqlError";
import useActiveOrganization from "../../components/common/hooks/useActiveOrganization";
import OrganizationImportModal from "../../components/organization/OrganizationImportModal";
import ImportedAccountActivationPanel from "../../components/organization/ImportedAccountActivationPanel";

export default function DivisionPage() {
    const { activeProfile } = useActiveProfile();
    const colors = useThemeColors();
    const { t } = useTranslation();

    const params = useParams();
    const location = useLocation();
    const { organization, activeSubscription } = useActiveOrganization(params.orgSlug);
    const hasEnterpriseUserManagement = activeSubscription?.plan?.features?.some(
        (feature) => feature?.key === "enterprise_user_management"
    );
    const notifyEnterpriseFeatureRequired = () => toast.warning(t("subscriptionLimit.feature_required"), {
        autoClose: 5000,
        position: "top-center",
        closeOnClick: true,
    });

    const [divisions, setDivisions] = useState([]);
    const [waivers, setWaivers] = useState([]);
    const [divisionOrganizationId, setDivisionOrganizationId] = useState(null);

    console.log({
        organization,
        divisionOrganizationId,
        orgSlug: params.orgSlug,
    });

    const [totalPages, setTotalPages] = useState(1);

    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [divisionToEdit, setDivisionToEdit] = useState(null);

    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState(DIVISION_STATUS_FILTERS.ALL);
    const pageSize = 12;

    const [removeDivMutation] = ArchiveDivisionMutation();

    const { loading, error } = useQuery(GET_DIVISIONS, {
        variables: {
            organizationSlug: params.orgSlug,
            profileId: activeProfile?.id,
            page,
            pageSize,
            statusFilter,
        },
        skip: !activeProfile || !params.orgSlug,
        fetchPolicy: "cache-and-network",
        onCompleted: (data) => {
            setDivisions(data?.divisions?.objects || []);
            setDivisionOrganizationId(data?.organization?.id || data?.divisions?.objects?.[0]?.organization?.id || null);
            setTotalPages(data?.divisions?.pages || 1);
            setWaivers(location.state?.waivers || []);
        },
    });

    const visibleDivisions = divisions;

    const handleStatusFilterChange = (nextFilter) => {
        setStatusFilter(nextFilter);
        setPage(1);
    };

    const checkLimitOrPrompt = useCheckLimitOrPrompt(activeSubscription);

    const handleAddDivision = () => {
        const allowed = checkLimitOrPrompt({
            currentCount: divisions.length,
            itemLabel: "divisions",
            toast,
        });
        if (!allowed) return;
        setShowModal(true);
    };

    const archiveDivision = async (divId) => {
        try {
            const res = await removeDivMutation({
                variables: { divisionId: parseInt(divId, 10) },
            });

            if (res.data.archiveDivision.success) {
                setDivisions((prev) => prev.filter((div) => div.id !== divId));

                toast.success(t("divisions.archiveSuccess"), {
                    autoClose: 3000,
                    toastId: "remove_div_success",
                    position: "top-center",
                    closeOnClick: true,
                });
            } else {
                toast.error(t("divisions.archiveFailed"), {
                    autoClose: 3000,
                    toastId: "remove_div_failed",
                    position: "top-center",
                    closeOnClick: true,
                });
            }
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "divisions.archiveError"), {
                autoClose: 3000,
                toastId: "remove_div_error",
                position: "top-center",
                closeOnClick: true,
            });
        }
    };

    const requestArchiveDivision = (division) => {
        confirmDelete({
            title: t("divisions.confirmArchiveTitle"),
            message: t("divisions.confirmArchiveMessage"),
            confirmLabel: t("divisions.archive"),
            cancelLabel: t("divisions.cancel"),
            danger: true,
            onConfirm: async () => {
                await archiveDivision(division.id);
            },
        });
    };

    const editDivision = (divId) => {
        const division = divisions.find((div) => div.id === divId);
        setDivisionToEdit(division);
        setShowEditModal(true);
    };

    const navBarElem = {
        pageTitle: t("organizationDivisionSummary.title"),
        links: [
            {
                pageTitle: t("organizations.pageTitle"),
                navLink: "/management/organizations",
                active: false,
            },
            {
                pageTitle: `${
                    divisions[0]?.organization?.name || t("divisions.organizationFallback")
                } - ${t("organizationDivisionSummary.title")}`,
                navLink: null,
                active: true,
            },
        ],
    };

    const cardStyles = useMemo(() => {
        return {
            card: {
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 14px 34px rgba(0,0,0,0.10)",
                transition:
                    "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 12px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                color: colors.textOnPrimary,
            },
            headerLeft: { display: "flex", alignItems: "center", gap: 8 },
            chip: {
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 10px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.02em",
                background: colors.secondary,
                border: "1px solid rgba(0,0,0,0.16)",
                color: colors.textOnSecondary,
            },
            statusChipOpen: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 900,
                background: colors.success,
                color: colors.white,
                border: "1px solid rgba(25,135,84,0.25)",
            },
            statusChipClosed: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 900,
                background: colors.surface,
                color: colors.textOnSurface,
                border: "1px solid rgba(108,117,125,0.25)",
            },
            body: { padding: 12, background: "#fff" },
            title: {
                margin: 0,
                fontSize: 16,
                fontWeight: 900,
                color: "#14151a",
                lineHeight: 1.15,
            },
            sub: {
                marginTop: 6,
                fontSize: 12.5,
                fontWeight: 800,
                color: "rgba(20,21,26,0.55)",
            },
            metaGrid: {
                marginTop: 10,
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 8,
            },
            metaRow: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 10px",
                borderRadius: 12,
                background: "rgba(0,0,0,0.02)",
                border: "1px solid rgba(0,0,0,0.06)",
            },
            metaLabel: {
                fontSize: 12,
                fontWeight: 900,
                color: "rgba(20,21,26,0.60)",
                letterSpacing: "0.02em",
                textTransform: "uppercase",
            },
            metaValue: {
                fontSize: 12.5,
                fontWeight: 900,
                color: "rgba(20,21,26,0.90)",
            },
            footer: {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
                padding: "10px 12px",
                background: "rgba(0,0,0,0.015)",
                borderTop: "1px solid rgba(0,0,0,0.06)",
            },
            actions: {
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginLeft: "auto",
            },
            btn: {
                borderRadius: 12,
                padding: "8px 10px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.10)",
            },
            btnPrimary: {
                background: colors.primary,
                color: colors.textOnPrimary,
            },
            btnGhost: {
                background: colors.surface,
                color: colors.textOnSurface,
            },
            btnDanger: {
                background: "rgba(220,53,69,0.08)",
                border: "1px solid rgba(220,53,69,0.25)",
                color: "rgba(220,53,69,1)",
            },
            iconBtn: {
                width: 36,
                height: 36,
                borderRadius: 12,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#fff",
                border: "1px solid rgba(0,0,0,0.10)",
            },
        };
    }, [colors]);

    return (
        <>
            <div className="content-wrapper">
                <NavBar navBarElem={navBarElem} />

                <section className="content">
                    {error && (
                        <div className="alert alert-danger col-6 mx-auto text-center">
                            {getLocalizedGraphQLError(error, t, "divisions.loadError")}
                        </div>
                    )}

                    <OrganizationDivisionSummaryCharts
                        organizationSlug={params.orgSlug}
                    />

                    <div className="container-fluid">
                        <div className="d-flex justify-content-end mb-3" style={{ gap: 8, flexWrap: "wrap" }}>
                            <OrganizationImportModal
                                organizationId={organization?.id || divisionOrganizationId}
                                canUseFeature={hasEnterpriseUserManagement}
                                onFeatureUnavailable={notifyEnterpriseFeatureRequired}
                            />
                            <ImportedAccountActivationPanel
                                organizationId={organization?.id || divisionOrganizationId}
                                canUseFeature={hasEnterpriseUserManagement}
                                onFeatureUnavailable={notifyEnterpriseFeatureRequired}
                            />
                        </div>

                        <PaginatedCardList
                            className="kbola-divisionList"
                            title={t("divisions.pageTitle")}
                            headerContent={
                                <DivisionStatusFilter
                                    value={statusFilter}
                                    onChange={handleStatusFilterChange}
                                    disabled={loading}
                                />
                            }
                            onAdd={handleAddDivision}
                            page={page}
                            pages={totalPages}
                            onPageChange={setPage}
                            items={visibleDivisions}
                            loading={loading}
                            renderItem={(division) => {
                            const isOpen = !!division.isOpen;
                            const hasSeason = !!division.activeSeason;

                            return (
                                <div
                                    className="col-12 col-sm-6 col-md-3 mb-3 d-flex align-items-stretch flex-column"
                                    key={division.slug}
                                >
                                    <div
                                        className="card d-flex flex-fill"
                                        style={cardStyles.card}
                                    >
                                        <div style={cardStyles.header}>
                                            <div style={cardStyles.headerLeft}>
                                                <span
                                                    style={cardStyles.chip}
                                                    title={t("divisions.sport")}
                                                >
                                                    <SportIcon
                                                        sportKey={division?.sport?.key || "soccer"}
                                                        className="text-lg"
                                                    />
                                                    {division?.sport?.name || t("divisions.sportFallback")}
                                                </span>
                                            </div>

                                            <span
                                                style={
                                                    isOpen
                                                        ? cardStyles.statusChipOpen
                                                        : cardStyles.statusChipClosed
                                                }
                                            >
                                                <i
                                                    className={
                                                        isOpen
                                                            ? "fas fa-check-circle"
                                                            : "fas fa-lock"
                                                    }
                                                />
                                                {isOpen
                                                    ? t("divisions.open").toUpperCase()
                                                    : t("divisions.closed").toUpperCase()}
                                            </span>
                                        </div>

                                        <div style={cardStyles.body}>
                                            <h3 style={cardStyles.title}>
                                                {division.name}
                                            </h3>

                                            <div style={cardStyles.sub}>
                                                {division?.organization?.name
                                                    ? `${t("divisions.organizationLabel")}: ${division.organization.name}`
                                                    : " "}
                                            </div>

                                            <div style={cardStyles.metaGrid}>
                                                {hasSeason ? (
                                                    <>
                                                        <div style={cardStyles.metaRow}>
                                                            <span style={cardStyles.metaLabel}>
                                                                {t("divisions.seasonStart")}
                                                            </span>
                                                            <span style={cardStyles.metaValue}>
                                                                {division.activeSeason.startDate}
                                                            </span>
                                                        </div>

                                                        <div style={cardStyles.metaRow}>
                                                            <span style={cardStyles.metaLabel}>
                                                                {t("divisions.seasonEnd")}
                                                            </span>
                                                            <span style={cardStyles.metaValue}>
                                                                {division.activeSeason.endDate}
                                                            </span>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div style={cardStyles.metaRow}>
                                                        <span style={cardStyles.metaLabel}>
                                                            {t("divisions.season")}
                                                        </span>
                                                        <span
                                                            style={cardStyles.metaValue}
                                                            title={t("divisions.noActiveSeasonConfigured")}
                                                        >
                                                            {t("divisions.noneActive")}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div style={cardStyles.footer}>
                                            <NavLink
                                                state={{ waivers }}
                                                to={`/management/organizations/${params.orgSlug}/divisions/${division.slug}`}
                                                className="btn"
                                                style={{
                                                    ...cardStyles.btn,
                                                    ...(division.isOpen
                                                        ? cardStyles.btnPrimary
                                                        : cardStyles.btnGhost),
                                                }}
                                                title={t("divisions.openDetails")}
                                            >
                                                <i className="fas fa-boxes" />
                                                {t("divisions.details")}
                                            </NavLink>

                                            <div style={cardStyles.actions}>
                                                <button
                                                    type="button"
                                                    className="btn"
                                                    style={cardStyles.iconBtn}
                                                    onClick={() => editDivision(division.id)}
                                                    title={t("divisions.editDivision")}
                                                >
                                                    <i className="fas fa-edit" />
                                                </button>

                                                <button
                                                    type="button"
                                                    className="btn"
                                                    style={{
                                                        ...cardStyles.iconBtn,
                                                        ...cardStyles.btnDanger,
                                                    }}
                                                    onClick={() => requestArchiveDivision(division)}
                                                    title={t("divisions.archiveDivision")}
                                                >
                                                    <i className="fas fa-trash" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                            }}
                        />
                    </div>
                </section>
            </div>

            <AddDivisionModal
                divisions={divisions}
                setDivisions={setDivisions}
                orgSlug={params.orgSlug}
                show={showModal}
                onClose={() => setShowModal(false)}
                waivers={waivers}
            />

            <EditDivisionModal
                divisions={divisions}
                setDivisions={setDivisions}
                divisionToEdit={divisionToEdit}
                show={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setDivisionToEdit(null);
                }}
                waivers={waivers}
            />

            <style>{`
                .content .card[style] {
                  background: #fff;
                }
                .content .card[style]:hover {
                  transform: translateY(-2px);
                  box-shadow: 0 18px 42px rgba(0,0,0,0.14) !important;
                  border-color: rgba(0,0,0,0.12) !important;
                }
                .kbola-divisionList .row {
                  margin-left: -7.5px !important;
                  margin-right: -7.5px !important;
                }
            `}</style>
        </>
    );
}
